<template>
  <div>
    <PurchaseResume
      :mode="mode"
      :school-reference="schoolReference"
      :purchase-data="purchaseData"
    />
    <h3>Billing Information</h3>
    <div>As shown in your credit card</div>
    <div>
      <form @submit.prevent="submit">
        <fieldset :disabled="status == 2">
          <FormInput v-model="formData.fields.sage_first_name">
            <label> First Name* </label>
            <input
              type="text"
              v-model="formData.fields.sage_first_name.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.sage_last_name">
            <label> Second Name* </label>
            <input
              type="text"
              v-model="formData.fields.sage_last_name.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.sage_address1">
            <label> Address* </label>
            <input
              type="text"
              v-model="formData.fields.sage_address1.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.sage_address2">
            <label> Address Line 2 </label>
            <input
              type="text"
              v-model="formData.fields.sage_address2.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.sage_country">
            <label> Country* </label>
            <select
              v-model="formData.fields.sage_country.value"
              class="sf-form-control tw-w-full"
            >
              <option v-for="c in countries" :key="c.id" :value="c.countryCode">
                {{ c.countryName }}
              </option>
            </select>
          </FormInput>
          <FormInput
            v-if="formData.fields.sage_country.value == 'US'"
            v-model="formData.fields.sage_state"
          >
            <label> State </label>
            <select
              v-model="formData.fields.sage_state.value"
              class="sf-form-control tw-w-full"
            >
              <option v-for="s in usStateOptions" :key="s.id" :value="s.code">
                {{ s.name }}
              </option>
            </select>
          </FormInput>
          <FormInput v-model="formData.fields.sage_city">
            <label> City* </label>
            <input
              type="text"
              v-model="formData.fields.sage_city.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.sage_postcode">
            <label> Postcode / ZIP code* </label>
            <input
              type="text"
              v-model="formData.fields.sage_postcode.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.sage_terms">
            <label>
              <input
                type="checkbox"
                v-model="formData.fields.sage_terms.value"
              />
              I have read, understood, and agree to the
              <a href="/sitemedia/Spellingframe_Terms_and_Conditions_FULL_v1.pdf" target="sf_tos"
                >terms and conditions</a
              >
            </label>
          </FormInput>
        </fieldset>
        <div>
          <button 
            type="submit" 
            :disabled="!formData.fields.sage_terms.value" 
            class="sf-btn-primary"
          >
            Purchase
          </button>
          <SfRouterLink
            v-if="invoiceId"
            to="/dashboard"
            class="sf-btn-secondary"
          >
            Cancel
          </SfRouterLink>
          <button
            v-else-if="showModify"
            type="button"
            @click.prevent="$emit('modify')"
            class="sf-btn-secondary"
          >
            Modify
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import submitForm from "@/func/form/submitForm";
import apiCall from "@/func/net/apiCall";
import {
  SfFormData,
  SfPurchaseType,
  SfCountry,
  SfUSState,
  SfStorePurchaseData,
} from "@/types/generalTypes";
import { defineComponent, PropType } from "vue";
import FormInput from "../ui/FormInput.vue";
import PurchaseResume from "./PurchaseResume.vue";
import SfRouterLink from "../shared/SfRouterLink.vue";

export default defineComponent({
  components: { PurchaseResume, FormInput, SfRouterLink },
  emits: ["modify"],
  props: {
    mode: { type: String as PropType<SfPurchaseType>, required: true },
    invoiceId: { type: Number },
    purchaseData: {
      type: Object as PropType<SfStorePurchaseData>,
      required: true,
    },
    schoolReference: String,
    country: String,
    userId: Number,
  },
  data() {
    return {
      status: 0 as 0 | 2 | -1,
      countries: [] as SfCountry[],
      usStates: null as SfUSState[] | null,
      formData: {
        fields: {
          sage_first_name: { value: "", required: true },
          sage_last_name: { value: "", required: true },
          sage_address1: { value: "", required: true },
          sage_address2: { value: "" },
          sage_country: { value: "", required: true },
          sage_city: { value: "", required: true },
          sage_state: { value: "", required: true },
          sage_postcode: { value: "", required: true },
          sage_terms: { value: "", required: true },
        },
      } as SfFormData,
    };
  },
  computed: {
    currentUserId() {
      return this.$currentUser.value?.id;
    },
    currentSchoolId() {
      return this.$currentUser.value?.school?.id;
    },
    usStateOptions(): SfUSState[] {
      if (!this.usStates) {
        this.refreshUSStates();
        return [];
      }
      return this.usStates;
    },
    apiUrl() {
      if (this.mode == "payinvoice") {
        return "purchase/pay-invoice";
      }
      if (this.mode == "individual") {
        return "purchase/individual";
      }
      return "purchase/school";
    },
    spellingClassCount() {
      if (!this.purchaseData) {
        return 0;
      }
      let res = 0;
      for (let p of this.purchaseData.products) {
        if (p.name == "classSpelling") {
          res += p.count;
        }
      }
      return res;
    },
    mathsClassCount() {
      if (!this.purchaseData) {
        return 0;
      }
      let res = 0;
      for (let p of this.purchaseData.products) {
        if (p.name == "classMaths") {
          res += p.count;
        }
      }
      return res;
    },
    individualProducts() {
      if (!this.purchaseData) {
        return [];
      }
      return this.purchaseData.products.map((p) => p.kind);
    },
    showModify() {
      if (!this.$currentUser.value?.school) {
        return false;
      }
      return !this.$currentUser.value?.school?.is_individual;
    }
  },
  mounted() {
    this.refreshCountries();
  },
  methods: {
    async submit() {
      if(this.formData.fields.sage_country.value == 'US') {
        this.formData.fields.sage_state.required = true;
      } else {
        this.formData.fields.sage_state.required = false;
        this.formData.fields.sage_state.value = "";
      }
      const extraFields = {
        paymentMethod: 2,
        invoiceId: this.invoiceId,
        schoolReference: this.schoolReference ? this.schoolReference : "",
        kind: this.mode,
        country: this.country,
        userId: this.userId ||this.currentUserId,
        schoolId: this.currentSchoolId,
        spellingClassCount: this.spellingClassCount,
        mathsClassCount: this.mathsClassCount,
        individualProducts: this.individualProducts,
      };
      const resData = await submitForm(this.apiUrl, this.formData, null, {
        extraFields,
      });
      this.formData = resData.formData;
      if (resData.response.success) {
        this.status = 2;
        if (resData.response?.data) {
          window.location.href = resData.response.data.nextUrl;
        }
      } else {
        this.status = -1;
      }
    },
    async refreshCountries() {
      const resData = await apiCall("country");
      if (resData.success) {
        this.countries = resData.data.items;
      }
      const countryCode = this.$currentUser.value?.school?.country_code;
      this.formData.fields.sage_country.value = countryCode || 'GB';
    },
    async refreshUSStates() {
      const resData = await apiCall("us-state");
      if (resData.success) {
        this.usStates = resData.data.items;
      }
    },
  },
});
</script>
